import React, { useEffect, useState, useCallback } from "react";
import { Button, Col, Row, Alert, Spinner } from "reactstrap";
import PropTypes from "prop-types";
import XplorPaySDK from "./XplorPaySDK";
import XPLORPAY_CONFIG from "./XplorPayConfig";
import { saveXplorPayAchToken } from "api/AutopayApi";
import { failAlert, successAlertCustom } from "utils/alertUtils";

/**
 * Component to save a bank account for AutoPay using XplorPay SDK
 * Gets JWT token from hosted ACH form, sends to backend to create vault token
 */
const XplorPaySaveAch = ({
  accountID,
  customerID,
  onSuccess,
  onError,
  toggle,
}) => {
  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const initSDK = async () => {
      try {
        // Initialize SDK with ACH enabled
        await XplorPaySDK.initForAch();
        if (mounted) setSdkReady(true);
      } catch (err) {
        if (mounted) setError(err.message);
      }
    };
    initSDK();
    return () => {
      mounted = false;
      XplorPaySDK.cleanup();
    };
  }, []);

  const handleSaveBank = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get token from SDK (ACH mode)
      const { jwt, lastFour } = await XplorPaySDK.getPaymentToken();

      // Send to backend to create vault token
      const response = await saveXplorPayAchToken({
        jwt,
        lastFour,
        accountId: accountID,
        dmsCustomerId: customerID,
        tokenType: "ach",
      });

      if (response.data?.content?.success) {
        successAlertCustom("Bank account saved successfully!");
        if (onSuccess) onSuccess(response.data.content);
        if (toggle) toggle();
      } else {
        throw new Error(response.data?.content?.errorMessage || response.data?.message || "Failed to save bank account");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to save bank account";
      setError(errorMessage);
      failAlert(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  }, [accountID, customerID, onSuccess, onError, toggle]);

  return (
    <div className="xplorpay-save-ach">
      <Row className="mb-3">
        <Col>
          <h5>Add Bank Account</h5>
          <p className="text-muted">Enter your bank account details to save for AutoPay</p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {!sdkReady && !error && (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-2">Loading secure form...</p>
        </div>
      )}

      {/* XplorPay hosted ACH form renders here */}
      <Row className="mb-3">
        <Col>
          <div
            id={XPLORPAY_CONFIG.paymentFormId}
            style={{
              minHeight: sdkReady ? "250px" : "0",
              border: sdkReady ? "1px solid #ddd" : "none",
              borderRadius: "4px",
              padding: sdkReady ? "15px" : "0",
              backgroundColor: "#f9f9f9",
            }}
          />
        </Col>
      </Row>

      {sdkReady && (
        <Row className="mt-3">
          <Col className="text-center">
            <Button color="primary" onClick={handleSaveBank} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" /> Saving...
                </>
              ) : (
                "Save Bank Account"
              )}
            </Button>
          </Col>
        </Row>
      )}

      <Row className="mt-3">
        <Col className="text-center">
          <small className="text-muted">
            <i className="fa fa-lock mr-1"></i>
            Bank data is encrypted and securely stored by XplorPay.
          </small>
        </Col>
      </Row>
    </div>
  );
};

XplorPaySaveAch.propTypes = {
  accountID: PropTypes.number.isRequired,
  customerID: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  toggle: PropTypes.func,
};

export default XplorPaySaveAch;
